import { router } from '@inertiajs/react'
import {
  ChevronLeft,
  Sparkles,
  Mail,
  Webhook,
  FileSpreadsheet,
  Calendar,
  MoreVertical,
  Archive,
  Trash2,
} from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import type { Gem } from '@/components/gems'

interface Props {
  gem: Gem
}

export default function GemsShow({ gem }: Props) {
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false)
      }
    }
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMenu])

  const handleBack = () => {
    router.visit('/gems')
  }

  const handleViewItem = () => {
    router.visit(`/items/${gem.itemId}`)
  }

  const handleArchive = () => {
    if (confirm('Are you sure you want to archive this gem?')) {
      router.post(`/gems/${gem.id}/archive`)
    }
  }

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this gem?')) {
      router.delete(`/gems/${gem.id}`)
    }
  }

  const handleSendEmail = async () => {
    if (!confirm('Send this gem to all stream subscribers?')) {
      return
    }

    try {
      const response = await fetch(`/gems/${gem.id}/send_email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
      })

      const data = await response.json()

      if (response.ok) {
        alert(data.message)
        router.reload()
      } else {
        alert(data.error || 'Failed to send email')
      }
    } catch (error) {
      alert('Failed to send email')
    }
  }

  const sourceIcons = {
    email: Mail,
    webhook: Webhook,
    csv: FileSpreadsheet,
  }
  const SourceIcon = sourceIcons[gem.sourceType] || Mail

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const formatDeliveryTime = (dateString: string) => {
    const date = new Date(dateString)
    const datePart = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    const hours = date.getHours()
    const minutes = date.getMinutes()
    const ampm = hours >= 12 ? 'pm' : 'am'
    const hour12 = hours % 12 || 12
    const timePart = minutes > 0
      ? `${hour12}:${minutes.toString().padStart(2, '0')}${ampm}`
      : `${hour12}${ampm}`
    return `${datePart} · ${timePart}`
  }

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      {/* Header */}
      <header className="bg-white dark:bg-stone-900">
        <div className="max-w-4xl mx-auto px-6 py-5">
          {/* Breadcrumb */}
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-1 text-sm text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300 transition-colors mb-4"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Gems</span>
          </button>

          {/* Title row with actions */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <h1 className="text-2xl font-semibold text-stone-900 dark:text-stone-100 truncate">
                  {gem.subject}
                </h1>
              </div>

              {/* Subtitle */}
              <div className="flex items-center gap-2 mt-2 text-sm text-stone-500 dark:text-stone-400">
                <button
                  onClick={() => router.visit(`/streams/${gem.streamId}`)}
                  className="hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                >
                  {gem.streamName}
                </button>
                <span className="text-stone-300 dark:text-stone-600">·</span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {formatDate(gem.flaggedAt)}
                </span>
                <span className="text-stone-300 dark:text-stone-600">·</span>
                <span className="capitalize flex items-center gap-1">
                  <SourceIcon className="w-3.5 h-3.5" />
                  {gem.sourceType}
                </span>
              </div>
            </div>

            {/* Actions dropdown */}
            <div className="relative flex-shrink-0" ref={menuRef}>
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-500 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-700 hover:text-stone-700 dark:hover:text-stone-300 transition-colors"
              >
                <MoreVertical className="w-4 h-4" />
              </button>

              {showMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-stone-800 rounded-xl shadow-lg border border-stone-200 dark:border-stone-700 py-1 z-50">
                  <button
                    onClick={() => {
                      handleSendEmail()
                      setShowMenu(false)
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    Email gem
                  </button>
                  <button
                    onClick={() => {
                      handleArchive()
                      setShowMenu(false)
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors"
                  >
                    <Archive className="w-4 h-4" />
                    Archive
                  </button>
                  <div className="my-1 border-t border-stone-100 dark:border-stone-700" />
                  <button
                    onClick={() => {
                      handleDelete()
                      setShowMenu(false)
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="space-y-6">
          {/* Matching criteria */}
          {gem.matchingCriteria && gem.matchingCriteria.length > 0 && (
            <section>
              <h2 className="text-xs font-medium text-stone-400 dark:text-stone-500 uppercase tracking-wide mb-3">
                Matched Criteria
              </h2>
              <div className="flex flex-wrap gap-2">
                {gem.matchingCriteria.map((criteria, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300"
                  >
                    <Sparkles className="w-4 h-4" />
                    {criteria}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Reason */}
          <section>
            <h2 className="text-xs font-medium text-stone-400 dark:text-stone-500 uppercase tracking-wide mb-3">
              Why this was flagged
            </h2>
            <div className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/50 p-6">
              <p className="text-sm text-stone-700 dark:text-stone-300 leading-relaxed">
                {gem.reason}
              </p>
            </div>
          </section>

          {/* Notes */}
          {gem.notes && (
            <section>
              <h2 className="text-xs font-medium text-stone-400 dark:text-stone-500 uppercase tracking-wide mb-3">
                Notes
              </h2>
              <div className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/50 p-6">
                <p className="text-sm text-stone-700 dark:text-stone-300 leading-relaxed">
                  {gem.notes}
                </p>
              </div>
            </section>
          )}

          {/* View original item link */}
          <section>
            <button
              onClick={handleViewItem}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/30 hover:bg-teal-100 dark:hover:bg-teal-900/50 rounded-lg transition-colors"
            >
              View original item
            </button>
          </section>

          {/* Delivery Log */}
          {gem.deliveries && gem.deliveries.length > 0 && (
            <section className="pt-6 border-t border-stone-200 dark:border-stone-800">
              <h2 className="text-xs font-medium text-stone-400 dark:text-stone-500 uppercase tracking-wide mb-4">
                Email Delivery Log
              </h2>
              <div className="space-y-2">
                {gem.deliveries.map((delivery) => (
                  <div
                    key={delivery.id}
                    className="flex items-center justify-between py-2 px-3 rounded-lg bg-stone-50 dark:bg-stone-800/50"
                  >
                    <div className="flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5 text-teal-500" />
                      <span className="text-sm text-stone-700 dark:text-stone-300">
                        {delivery.userName}
                      </span>
                      <span className="text-sm text-stone-400 dark:text-stone-500">
                        {delivery.userEmail}
                      </span>
                    </div>
                    <span className="text-xs text-stone-400 dark:text-stone-500">
                      {formatDeliveryTime(delivery.deliveredAt)}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
    </div>
  )
}
